import _taggedTemplateLiteral from "@babel/runtime/helpers/esm/taggedTemplateLiteral";
var _templateObject, _templateObject2;
/* eslint-disable no-nested-ternary */
import React from 'react';
import gql from 'graphql-tag';
import _ from 'lodash';
import { Input, Message, Button, Form, Grid, Icon, Accordion, Tab, Checkbox, Divider, Popup } from 'semantic-ui-react';
import { withFormik } from 'formik';
import { AuthClient, getProfileAvatar } from '@emyw/core-lib';
import ButtonLink from '../ButtonLink';
import { normalizeErrors, isOpera, isChrome, isBlink } from '../utils';
import PinRestrictions from './PinRestrictions';
const upsertProfileMutation = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    mutation ($id: Int, $userId: Int!, $profileInput: ProfileInput!) {\n        upsertProfile(id: $id, userId: $userId, profileInput: $profileInput) {\n            ok\n            id\n            errors {\n                path\n                message\n            }\n        }\n    }  \n"])));
const setAvatarMutation = gql(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    mutation ($profileId: Int!, $avatarBase64: String!) {\n        setAvatar(profileId: $profileId, avatarBase64: $avatarBase64) {\n            ok\n            avatarUrl\n            errors {\n                path\n                message\n            }\n        }\n    }\n"])));
const EditProfileLogic = withFormik({
  validateOnBlur: false,
  validateOnChange: false,
  // Transform outer props into form values
  mapPropsToValues: _ref => {
    let {
      profile
    } = _ref;
    return {
      name: profile.name,
      // dateOfBirth: formatISODate(profile.dateOfBirth),
      contentPINRestricted: profile.contentPINRestricted,
      filterChangesNeedPIN: profile.filterChangesNeedPIN,
      securedByPIN: profile.securedByPIN,
      pin: profile.pin,
      showPin: false
    };
  },
  // Add a custom validation function (this can be async too!)
  validate: values => {
    const errors = {};
    const {
      name,
      securedByPIN,
      pin /* , dateOfBirth, */
    } = values;
    // Profile Name
    if (!name) {
      errors.name = 'Profile name required';
    } else if (name.length < 2) {
      errors.name = 'Profile name must be at least 2 characters';
    }
    if (securedByPIN && pin && pin.length !== 4) {
      errors.pin = 'PIN must be 4 digits long';
    }
    // Date of Birth
    // if (dateOfBirth && !Date.parse(dateOfBirth)) {
    //     errors.dateOfBirth = 'Date of birth must be valid and in format MM/DD/YYYY';
    // }
    return errors;
  },
  // Submission handler
  handleSubmit: async (values, _ref2) => {
    let {
      props: {
        profile,
        newAvatarUrl,
        onClose
      },
      // setSubmitting, setErrors /* , setFieldValue */
      setErrors,
      setSubmitting
    } = _ref2;
    const profileInput = {
      name: values.name,
      contentPINRestricted: values.contentPINRestricted,
      filterChangesNeedPIN: values.filterChangesNeedPIN,
      securedByPIN: values.securedByPIN,
      pin: values.pin
    };
    // if ((values.dateOfBirth || '') !== '' && Date.parse(values.dateOfBirth)) {
    //     profileInput.dateOfBirth = new Date(values.dateOfBirth).toISOString();
    // }
    try {
      // Try calling upsertProfileMutation
      let response = await AuthClient.mutate({
        mutation: upsertProfileMutation,
        variables: {
          id: profile.id,
          userId: profile.userId,
          profileInput
        }
      });
      const {
        ok,
        errors,
        id
      } = response.data.upsertProfile;
      if (ok) {
        // Try calling setAvatarMutation
        if (newAvatarUrl) {
          try {
            response = await AuthClient.mutate({
              mutation: setAvatarMutation,
              variables: {
                profileId: id,
                avatarBase64: newAvatarUrl
              }
            });
            const {
              ok: avatarOk,
              errors: avatarErrors
            } = response.data.setAvatar;
            if (avatarOk) {
              if (onClose) onClose(true, id);else log.warn('onClose prop required but not provided');
            } else {
              setSubmitting(false);
              setErrors({
                avatar: [avatarErrors[0].message]
              });
            }
          } catch (error) {
            setSubmitting(false);
            setErrors({
              unknown: [error.message]
            });
          }
        } else if (onClose) {
          onClose(true, id);
        } else {
          log.warn('onClose prop required but not provided');
        }
      } else {
        setSubmitting(false);
        setErrors(normalizeErrors(errors));
      }
    } catch (error) {
      setSubmitting(false);
      setErrors({
        unknown: [error.message]
      });
    }
  }
});

/** Manage Profiles Form allows for maintenance of user profiles.
 * <br /><br /><hr /><br />
 * <div>
 * <h4>Back-end Requirements</h4>
 * A backend graphql api endpoint with the upsertProfile mutation,
 * <a rel=noopener href="https://github.com/emyw/auth-api">auth-api</a>,
 * is needed for this form to function.
 * <h4>Manage Profiles</h4>
 * This allows for updating of the basic profiles data for the user.
 * </div>
 */
const EditProfileForm = _ref3 => {
  let {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setValues,
    touched,
    profile,
    newAvatarUrl,
    onClose,
    onChangePicture,
    PreferenceContent,
    errors,
    enablePINSettings
  } = _ref3;
  const preferenceContent = PreferenceContent ? PreferenceContent(setFieldValue) : null;
  // use this to stop chrome from prompting to save these as passwords
  // If not chrome, then we'll just set the type to password (i.e. secret = true). If it is chrome, we'll use the
  // textSecurity: 'disc' trick for a input type='text', which doesn't work in other browsers.
  const useSecret = !(isChrome || isOpera || isBlink);
  if (preferenceContent && preferenceContent.props.panes) {
    preferenceContent.props.panes.push({
      menuItem: 'PIN Restrictions',
      render: () => {
        const handleContentPINRestrictedUpdate = value => {
          setFieldValue('contentPINRestricted', value.allowedCodes === 'ALL' ? 'NONE' : value.code);
        };
        const handleFilterChangesNeedPINUpdate = value => {
          setFieldValue('filterChangesNeedPIN', value);
        };
        return /*#__PURE__*/React.createElement(Tab.Pane, null, /*#__PURE__*/React.createElement(PinRestrictions, {
          contentPINRestricted: values.contentPINRestricted,
          filterChangesNeedPIN: values.filterChangesNeedPIN,
          onContentPINRestrictedUpdate: handleContentPINRestrictedUpdate,
          onFilterChangesNeedPINUpdate: handleFilterChangesNeedPINUpdate,
          disabled: !enablePINSettings
        }));
      }
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "manageProfile_dialog"
  }, /*#__PURE__*/React.createElement(Accordion, {
    className: "profileDetails",
    defaultActiveIndex: 0,
    panels: [{
      key: 'profile-details',
      title: 'Profile Details',
      content: {
        content: /*#__PURE__*/React.createElement("div", {
          className: "fixedRow middle manageProfile_form"
        }, /*#__PURE__*/React.createElement("div", {
          className: "fixedCol form_avatar"
        }, /*#__PURE__*/React.createElement(ButtonLink, {
          className: "profile_avatar_button",
          onClick: () => {
            if (onChangePicture) onChangePicture();else log.warn('onChangePicture prop required but not provided');
          }
        }, /*#__PURE__*/React.createElement("span", {
          className: "profile_avatar_label"
        }, "Change"), getProfileAvatar({
          profile: {
            ...profile,
            name: values.name
          },
          iconSize: 'huge',
          newAvatarUrl
        }))), /*#__PURE__*/React.createElement("div", {
          className: "fixedCol profileInputs"
        }, /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Field, {
          error: !!errors.name,
          width: 16
        }, /*#__PURE__*/React.createElement(Input, {
          name: "name",
          id: "name",
          onChange: handleChange,
          onBlur: handleBlur,
          defaultValue: values.name || ''
          // defaultValue={defaultName}
          ,
          icon: "id card outline",
          iconPosition: "left",
          placeholder: "Profile Name",
          fluid: true
        }))), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(Form.Field, {
          width: 12
        }, /*#__PURE__*/React.createElement(Popup, {
          className: "enjoyPopup featureDialogPopup featureDescription",
          trigger: /*#__PURE__*/React.createElement(Checkbox, {
            toggle: true,
            name: "securedByPIN",
            id: "securedByPIN",
            checked: Boolean(enablePINSettings && values.securedByPIN),
            disabled: !enablePINSettings,
            onClick: (e, data) => {
              // e.preventDefault();
              setFieldValue(data.id, data.checked);
            },
            style: {
              margin: '0.6em'
            },
            onKeyPress: e => {
              if (e.charCode === 32) {
                e.target.checked = !e.target.checked;
              }
            },
            label: "Require PIN to Select Profile"
          }),
          wide: true,
          position: "top center",
          content: enablePINSettings ? 'Enable to require a PIN number when selecting this profile' : 'This feature requires an account level PIN to be set first'
        })), /*#__PURE__*/React.createElement(Form.Field, {
          disabled: !enablePINSettings,
          error: !!errors.pin || !!errors.cryptPIN,
          width: 4,
          autoComplete: "off"
        }, /*#__PURE__*/React.createElement(Popup, {
          className: "enjoyPopup featureDialogPopup featureDescription",
          trigger: /*#__PURE__*/React.createElement(Input, {
            className: "setProfilePinInput",
            name: "pin",
            id: "pin",
            onChange: (event, data) => {
              const numericOnly = event.target.value.replace(/\D/, '');
              handleChange({
                ...event,
                target: {
                  ...event.target,
                  value: numericOnly
                }
              });
              setFieldValue(data.id, numericOnly);
            },
            onBlur: handleBlur,
            value: enablePINSettings && (values.pin || ''),
            placeholder: "Profile PIN",
            icon: true,
            iconPosition: "left",
            fluid: true,
            disabled: !values.securedByPIN,
            maxLength: "4",
            style: !useSecret && !values.showPin ? {
              textSecurity: 'disc',
              WebkitTextSecurity: 'disc'
            } : {},
            type: useSecret ? values.showPin ? 'text' : 'password' : 'text'
          }, /*#__PURE__*/React.createElement(Icon, {
            name: "lock",
            style: {
              textSecurity: 'none',
              WebkitTextSecurity: 'none'
            }
          }), /*#__PURE__*/React.createElement("input", {
            "data-lpignore": true,
            autoComplete: "new-password"
          }), values.pin !== '****' && /*#__PURE__*/React.createElement(Icon, {
            name: values.showPin ? 'eye slash' : 'eye',
            link: true,
            style: {
              textSecurity: 'none',
              WebkitTextSecurity: 'none'
            },
            disabled: !values.securedByPIN || !touched.pin,
            onClick: () => setValues({
              ...values,
              showPin: !values.showPin
            })
          })),
          wide: true,
          position: "top center",
          content: 'Optional: When secured by PIN, switching to this profile will prompt for a PIN number.' + '\nEntering this optional PIN or the account level PIN will allow this profile to be ' + 'selected.'
        })))), touched.name && errors.name || touched.pin && (errors.pin || errors.cryptPIN) || errors.unknown ? /*#__PURE__*/React.createElement(Message, {
          size: "small",
          error: true,
          header: "Trouble with form...",
          list: _.concat(errors.name, errors.pin, errors.cryptPIN, errors.avatar, errors.unknown)
        }) : /*#__PURE__*/React.createElement("br", null)))
      }
    }]
  }), /*#__PURE__*/React.createElement(Accordion, {
    className: "globalPreferences",
    defaultActiveIndex: 0,
    panels: [{
      key: 'global-preferences',
      title: 'Global Preferences',
      content: {
        content: preferenceContent
      }
    }]
  }), /*#__PURE__*/React.createElement(Grid, {
    columns: 2,
    className: "actionButtons"
  }, /*#__PURE__*/React.createElement(Grid.Row, {
    textAlign: "center",
    verticalAlign: "middle"
  }, /*#__PURE__*/React.createElement(Grid.Column, {
    width: "8"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    disabled: isSubmitting,
    basic: true,
    color: "grey",
    fluid: true,
    onClick: () => {
      if (onClose) onClose(false);else log.warn('onClose prop required but not provided');
    }
  }, "Discard Changes")), /*#__PURE__*/React.createElement(Grid.Column, {
    width: "8"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    disabled: isSubmitting,
    onClick: handleSubmit,
    basic: true,
    color: "blue",
    fluid: true
  }, "Submit")))));
};
const EditProfileFormElement = EditProfileLogic(EditProfileForm);

// Need a class with state so uploading new avatar image doesn't reset form fields
export default class EditProfileDialog extends React.Component {
  constructor(props, context) {
    super(props, context);
    if (!props.PreferenceContent) log.warn('PreferenceContent prop required but not provided');
  }
  render() {
    return /*#__PURE__*/React.createElement(EditProfileFormElement, this.props);
  }
}

/* eslint-disable react/no-unused-prop-types */

EditProfileDialog.defaultProps = {
  newAvatarUrl: null,
  PreferenceContent: () => /*#__PURE__*/React.createElement("br", null),
  enablePINSettings: false
};